import type { DateRangeValue } from '@components/date-range';

import { toDateRangeBounds } from './relative-date';

/**
 * Encodes a single non-empty filter value into 0..N URL params. Receives the
 * value (already known to be non-null/undefined — empties are filtered by
 * {@link serializeFilters} before this is called) and the filter's own key,
 * which encoders default to as the param name. Return `undefined` to skip
 * the field, or a record to merge into the output params.
 *
 * `T` is the value's type **after** null/undefined have been removed; the
 * encoder map parameter on {@link serializeFilters} applies `NonNullable<>`
 * to each filter property, so an encoder declared for `string` slots into a
 * filter property typed `string | undefined` without an explicit cast.
 */
export type FilterParamEncoder<T> = (
  value: T,
  key: string,
) => Record<string, unknown> | undefined;

const string: FilterParamEncoder<string> = (value, key) =>
  value ? { [key]: value } : undefined;

const number: FilterParamEncoder<number> = (value, key) => ({
  [key]: String(value),
});

const boolean: FilterParamEncoder<boolean> = (value, key) => ({
  [key]: String(value),
});

const stringArray: FilterParamEncoder<readonly string[]> = (value, key) =>
  value.length > 0 ? { [key]: value } : undefined;

const jsonRecord: FilterParamEncoder<Record<string, string>> = (value, key) =>
  Object.keys(value).length > 0 ? { [key]: JSON.stringify(value) } : undefined;

/**
 * Maps a `DateRangeValue` filter to `${prefix}After`/`${prefix}Before` ISO
 * params. The prefix is supplied explicitly because the filter key (e.g.
 * `createdAt`) often differs from the URL param prefix (e.g. `created`).
 */
const dateRange =
  (prefix: string): FilterParamEncoder<DateRangeValue> =>
  value => {
    const [after, before] = toDateRangeBounds(value);
    const params: Record<string, unknown> = {};

    if (after) params[`${prefix}After`] = after;
    if (before) params[`${prefix}Before`] = before;

    return Object.keys(params).length > 0 ? params : undefined;
  };

/**
 * Library of common filter-param encoders. Use the bare ones for simple
 * presence-guarded mappings; use the factories (e.g. {@link dateRange}) when
 * the param shape needs configuration that can't be derived from the filter
 * key alone.
 */
export const filterEncoders = {
  boolean,
  dateRange,
  jsonRecord,
  number,
  string,
  stringArray,
};

/**
 * Translates a filter object into a URL params record using a per-key encoder
 * map. Skips entries where the filter value is `undefined`/`null` or where
 * the encoder returns `undefined`. Output ordering follows the encoder map's
 * declaration order, not the input filter object's key order.
 */
export const serializeFilters = <T extends object>(
  filters: T | undefined,
  encoders: Partial<{
    [K in keyof T]: FilterParamEncoder<NonNullable<T[K]>>;
  }>,
): Record<string, unknown> => {
  if (!filters) return {};

  const params: Record<string, unknown> = {};

  (
    Object.entries(encoders) as [
      keyof T,
      FilterParamEncoder<NonNullable<T[keyof T]>> | undefined,
    ][]
  ).forEach(([key, encoder]) => {
    if (!encoder) return;

    const value = filters[key];
    if (value === undefined || value === null) return;

    const encoded = encoder(value as NonNullable<T[typeof key]>, key as string);
    if (encoded) Object.assign(params, encoded);
  });

  return params;
};
